import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, Subject } from 'rxjs';
import { share } from 'rxjs/operators';

import { IRole, Role } from '../models/role';
import { User } from '../models/user';
import { Jwt, RoleType } from '../utilities/constants';
import { UtilityService } from './utilities.service';
import { AccountLogin } from '../../account/models/account-login';
import { SsoProvider } from '../enums/sso-provider.enum';
import { AuthResponse } from '../models/auth-response';
import { HttpClientType } from '../enums/http-client-type.enum';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

const helper = new JwtHelperService();

/** Service to handle user authentication. Use the UserService to perform user actions. */
@Injectable()
export class AuthService {
  decodedToken: any;
  username: string;
  userId: number;
  userEmail: string;
  redirectUrl: string;
  newAccountVerification: boolean;
  isEmailConfirmed: boolean;
  isEulaAccepted: boolean;
  inAcademySchool: boolean;
  isLoggedIn$: Subject<boolean> = new Subject<boolean>();
  isAuthenticated$: Subject<boolean> = new Subject<boolean>();
  errors$: Observable<string> = new Observable<string>(o => (this._errorObserver = o)).pipe(
    share()
  );

  private _errorObserver: any;
  private _roles: IRole[] = new Array<IRole>();
  firstName: string;
  lastName: string;

  constructor(
    private _http: HttpClient,
    private _utilService: UtilityService
  ) {
    // populate this service's properties from token data
    this.parseToken(this.getToken());

    // check and emit login status
    this.checkLoginStatus();
  }

  isLoggedIn() {
    return helper.isTokenExpired(this.getToken()) == false;
  }

  isAuthenticated() {
    return this.isLoggedIn(); // && this.isEulaAccepted;
  }

  needsEulaAccepted() {
    return this.isLoggedIn() && !this.isEulaAccepted;
  }

  login(user: User) {
    this.fetchLoginToken(user).subscribe(
      data => {
        const token = data.access_token;
        if (token) {
          this.removeToken();
          this.parseToken(token);
          this.setToken(token);
        }
        this.isLoggedIn$.next(this.isLoggedIn());
      },
      result => {
        this._errorObserver && this._errorObserver.next(result.error.error_description);
      }
    );
  }

  loginFromStudio(idToken: string) {
    this.fetchLoginTokenFromStudio(idToken).subscribe(
      data => {
        const token = data.access_token;
        if (token) {
          this.removeToken();
          this.parseToken(token);
          this.setToken(token);
        }
        this.isLoggedIn$.next(this.isLoggedIn());
      },
      result => {
        this._errorObserver && this._errorObserver.next(result.error.error_description);
      }
    );
  }

  loginFromManager(qParams: string) {
    return this.getLoginCredsFromManager(qParams);
  }

  logout() {
    this.removeToken();
    this.isLoggedIn$.next(false);
  }

  checkLoginStatus() {
    this.isLoggedIn$.next(this.isLoggedIn());
  }

  getRoleType() {
    return this.decodedToken['role'];
  }

  refreshToken(user: User) {
    this.login(user);
  }

  private getLoginCredsFromManager(qParams: string) {
    return this._http.post('api/account/managerlogin', JSON.stringify(qParams), httpOptions);
  }

  private fetchLoginToken(user: User) {
    const formData = new FormData();
    formData.append('grant_type', 'password');
    formData.append('password', user.Password);
    formData.append('email', user.Email);

    return this._http.post<AccountLogin>('authenticate', formData);
  }

  private fetchLoginTokenFromStudio(idToken: string) {
    const r = {
      grant_type: 'bearer_token',
    };
    const httpOptionsForm = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${idToken}`,
      }),
    };
    return this._http.post<AccountLogin>(
      'authenticate',
      this._utilService.serialize(r),
      httpOptionsForm
    );
  }

  authenticateViaSSO(token: string, ssoProvider: SsoProvider): Observable<AuthResponse> {
    const httpOptionsForm = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        clientType: HttpClientType.Identity,
      }),
    };
    return this._http.post<AuthResponse>(
      `SsoAuthenticate`,
      { token, ssoProvider },
      httpOptionsForm
    );
  }

  private parseToken(token: string) {
    if (token != null) {
      this.decodedToken = helper.decodeToken(token);
      this.username = this.decodedToken['unique_name'];
      this.userId = +this.decodedToken['nameid'];
      this.isEmailConfirmed = this.decodedToken['email_confirmed'] === 'True';
      this.userEmail = this.decodedToken['email'];
      this.isEulaAccepted = this.decodedToken['eula'] === 'True';
      this.inAcademySchool = this.decodedToken['in_academy_school'] === 'True';
      this.firstName = this.decodedToken['firstname'];
      this.lastName = this.decodedToken['lastname'];
      this.extractRoles();
    }
  }

  private getToken(): string {
    return localStorage.getItem(Jwt.TOKEN_ID);
  }

  private setToken(accessToken: string) {
    localStorage.setItem(Jwt.TOKEN_ID, accessToken);
  }

  private removeToken() {
    localStorage.removeItem(Jwt.TOKEN_ID);
  }

  private extractRoles() {
    let rawRoles: string[] = [];
    if (typeof this.decodedToken['role'] === 'string') {
      rawRoles.push(this.decodedToken['role']);
    } else {
      rawRoles = this.decodedToken['role'];
    }

    rawRoles.forEach(rawRole => {
      const role: IRole = new Role();
      switch (rawRole) {
        case 'MindplayEmployee':
          role.Type = RoleType.MINDPLAY_EMPLOYEE;
          break;
        case 'ProgramManager':
          role.Type = RoleType.PROGRAM_MANAGER;
          break;
        case 'Teacher':
          role.Type = RoleType.TEACHER;
          break;
      }
      this._roles.push(role);
    });
  }
}
